import { IComponentOptions, IOnChangesObjectOf, IAugmentedJQuery, ITimeoutService } from "angular";
import { GtmhubController } from "@gtmhub/core";
import { IGridColumn, IGridColumnView, GridColumnDefinitions } from "./models";
import { toKeyMap } from "@gtmhub/util";
import { handleOutsideMouseEvent, handleInsideMouseEvent } from "@gtmhub/shared/directives/close-on-mousedown";
import { GridFilteringBarComponentCtrl } from "../grid-filtering-bar.component";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import debounce from "lodash.debounce";

interface IGridColumnsComponentRequire {
  gridFilteringBar: GridFilteringBarComponentCtrl;
}

export interface IGridColumnsComponentBindings {
  definitions: GridColumnDefinitions;
  columns: IGridColumn[];
  columnsChange(): void;
  closeOnClick: string;
  debounce?: number;
}

/**
 * Allow selecting which columns to be visible for grid layouts (e.g., goals, metrics, employees). It must be
 * used within `grid-filtering-bar` as its parent.
 *
 * `definitions` contains column definitions.
 *
 * `columns` are the selected columns.
 *
 * `columns-change` is fired when columns are changed (select, unselect, re-arrange).
 *
 * `close-on-click` specifies in which selectors when clicked to close the popover.
 *
 * * `debounce` specifies the amount of time in miliseconds to wait before firing the next onColumnsChange event.
 *
 * @example
 * <grid-filtering-bar>
 *   <grid-columns
 *     definitions="$ctrl.columnDefinitions"
 *     columns="$ctrl.columns"
 *     columns-change="$ctrl.onColumnsChange()"
 *     debounce="1000">
 *   </grid-columns>
 * </grid-filtering-bar>
 */
export interface GridColumnsComponentCtrl extends IGridColumnsComponentBindings, IGridColumnsComponentRequire {}
export class GridColumnsComponentCtrl extends GtmhubController {
  columnViews: IGridColumnView[];
  isOpen: boolean;

  private debouncedColumnChange: () => void;

  static $inject = ["$element", "$timeout", "$rootScope"];

  constructor(private $element: IAugmentedJQuery, private $timeout: ITimeoutService, private $rootScope: ITraceRootScopeService) {
    super();
  }

  $onInit(): void {
    this.onDestroy(handleOutsideMouseEvent("mousedown", () => this.$timeout(() => (this.isOpen = false)), this.$element));

    if (this.closeOnClick) {
      this.onDestroy(handleInsideMouseEvent("click", () => this.$timeout(() => (this.isOpen = false)), ...this.closeOnClick.split(",")));
    }
  }

  $onChanges(onChangesObj: IOnChangesObjectOf<IGridColumnsComponentBindings>): void {
    if (onChangesObj.definitions || onChangesObj.columns) {
      const definitions = this.definitions || {};
      const columns = this.columns || [];

      const columnsMap = toKeyMap("fieldName", columns);

      const selectedViewColumns = columns
        .filter((column) => column.fieldName in definitions)
        .map<IGridColumnView>((column) => ({ definition: definitions[column.fieldName], selected: true }));

      const unselectedViewColumns = Object.values(definitions)
        .filter((definition) => !(definition.fieldName in columnsMap))
        .map<IGridColumnView>((definition) => ({ definition, selected: false }));

      this.columnViews = [...selectedViewColumns, ...unselectedViewColumns];

      if (onChangesObj.debounce) {
        if (onChangesObj.debounce.currentValue) {
          this.debouncedColumnChange = debounce(() => {
            this.columnsChange();
          }, onChangesObj.debounce.currentValue);
        } else {
          this.debouncedColumnChange = null;
        }
      }
    }
  }

  toggleColumn(column: IGridColumnView): void {
    this.$rootScope.traceAction("toggle_column", () => {
      if (!column.definition.readOnly) {
        column.selected = !column.selected;
        this.notifyColumnsChange();

        const trackingEvent = !column.selected ? "Column Removed" : "Column Added";
        this.gridFilteringBar.track(trackingEvent, { field_name: column.definition.trackingName || column.definition.displayName });
      }
    });
  }

  notifyColumnsChange(): void {
    // clear all items but keep the array reference
    this.columns.length = 0;

    for (const column of this.columnViews) {
      if (column.selected) {
        this.columns.push({
          fieldName: column.definition.fieldName,
        });
      }
    }

    if (this.debouncedColumnChange) {
      this.debouncedColumnChange();
    } else {
      this.columnsChange();
    }
  }
}

export const GridColumnsComponent: IComponentOptions = {
  templateUrl: require("./grid-columns.jade"),
  controller: GridColumnsComponentCtrl,
  require: { gridFilteringBar: "^" },
  bindings: {
    closeOnClick: "@",
    definitions: "<",
    columns: "<",
    columnsChange: "&",
    debounce: "<",
  },
};
